import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { DataTable } from "safesystem_zip-dbf-reader-lib";

import type { ApplicationState } from ".";
import type { FeatureCollection, GeoJsonProperties, Point } from "geojson";

export const parseDataTableToGeoJSON = (dt: DataTable, latField: string, lonField: string, propsFields: Record<string, string>): FeatureCollection | undefined => {
	const propsFieldsList = Object.keys(propsFields);
	const allFields = [latField, lonField, ...propsFieldsList];
	const sourceFieldNames = dt.columns.map(c => c.name);
	if (allFields.filter(f => sourceFieldNames.includes(f)).length === allFields.length) {
		const features = dt.rows.map((row, id) => {
			const properties: Record<string, string | number | Date> = {};
			for (const field of propsFieldsList) {
				if (row[field] === "" || row[field] === null) return;
				properties[propsFields[field]] = row[field];
			}
			properties.id = id;
			id += 1;
			return {
				type: "Feature",
				properties: properties as GeoJsonProperties,
				geometry: {
					type: "Point",
					coordinates: [row[lonField], row[latField]]
				} as Point
			};
		});
		return {
			type: "FeatureCollection",
			// @ts-expect-error seems valid: https://maplibre.org/maplibre-gl-js/docs/API/classes/GeoJSONSource/
			features: features.filter((f) => f !== undefined)
		}
	} else {
		throw new Error(`One or more required fields (${allFields.join(", ")}) not found in data.`);
	}
}

export interface XlsxData {
	rows: Record<string, string | number>[]
}

const mapKabco: Record<string, string> = {
	"Fatal (K)": "(K) Fatal Injury",
	"Evident, Incapacitating (A)": "(A) Suspected Serious Injury",
	"Evident Non-Incapacitating (B)": "(B) Suspected Minor/Visible Injury",
	"Possible/Complaint of Injury (C)": "(C) Possible Injury / Complaint"
};

export const parseXlsxRowsToGeoJSON = (dt: XlsxData, latField: string, lonField: string): FeatureCollection | undefined => {
	const propsFieldsList = Object.keys(dt.rows[0]).filter(f => f !== latField && f !== lonField);
	const features = dt.rows.map((row, id) => {
		const properties: Record<string, string | number | Date> = {};
		for (const field of propsFieldsList) {
			if (row[field] === "" || row[field] === null) return;
			let value = row[field];
			if (field === "Severity" && Object.keys(mapKabco).includes(value as string)) {
				value = mapKabco[value as string] as string;
			}
			if (field === "Date of Incident") {
				const [mm, dd, yyyy] = (value as string).split("/");
				value = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}T00:01:00.000`;
			}
			properties[field] = value;
		}
		properties.id = id;
		id += 1;
		return {
			type: "Feature",
			properties: properties as GeoJsonProperties,
			geometry: {
				type: "Point",
				coordinates: [row[lonField], row[latField]]
			} as Point
		};
	});
	return {
		type: "FeatureCollection",
		// @ts-expect-error seems valid: https://maplibre.org/maplibre-gl-js/docs/API/classes/GeoJSONSource/
		features: features.filter((f) => f !== undefined)
	}
}

const sliceName = "customCrashesSlice";
export interface CustomCrashesState {
	data?: FeatureCollection;
	count: number;
	showError: boolean;
}

const initialState: CustomCrashesState = {
	data: undefined,
	count: 0,
	showError: false,
};

const slice = createSlice({
	name: sliceName,
	initialState,
	reducers: {
		setData: (state, { payload }: PayloadAction<FeatureCollection | undefined>) => {
			state.data = payload;
			state.count = payload?.features?.length ? payload.features.length : 0;
		},
		setErrorState: (state, { payload }: PayloadAction<boolean>) => {
			state.showError = payload;
		}
	},
});

// ----------------
// exports
export const selectCustomCrashes = (state: ApplicationState) => state.customCrashesState.data;
export const selectCustomCrashCount = (state: ApplicationState) => state.customCrashesState.count;
export const selectCustomCrashShowError = (state: ApplicationState) => state.customCrashesState.showError;
export const actions = {
	...slice.actions,
};
export const reducer = slice.reducer;
